'use client'

// The code door — the half of the email that works EVERYWHERE.
//
// A magic link opens in whatever browser the mail app owns, which may not be
// the browser the person is actually standing in (an in-app webview, a work
// profile, another device). The code has no such opinion: it is typed back into
// the page that asked for it, verified right here with verifyOtp, and the
// session lands exactly where the person is. The email now carries both — one
// tap where links work, a few digits where they don't — and this component is the
// receiving side of the code.
//
// Mounted by every surface that sends the email (/login, /signup, BeginFlow, the
// birth's account door) in their "sent" state, so the flows stay one flow.
//
// ── THE SENT STATE MUST NEVER BE A DEAD END ──────────────────────────────────
// "check your email" is where a person lands after typing an address, and the
// address might be wrong, or the mail late, or in spam. So this state always has
// two quiet ways out, and both live here so no surface can forget them:
//   · "use a different address" — the surface hands `onChangeAddress`, which puts
//     the field back with what was typed still in it.
//   · "send it again" — after about half a minute, sends the same email again
//     (this component knows the address and where the link should land).
//
// ── THE CODE ─────────────────────────────────────────────────────────────────
// Numeric keypad, the OS's own one-time-code autofill. The LENGTH is Supabase's
// (6 to 10; the project sends eight), so nothing here counts to six any more: a
// paste or an autofill is checked at once ("1234 5678" and "your code is
// 12345678" both work), and typing is checked when it settles or at ten digits.
// A wrong code keeps the digits, so a slip of one digit is a correction, not a
// retype. Each new email retires the code before it, and the error says so.

import React, { useEffect, useRef, useState } from 'react'
import { createClient } from '../lib/supabase/client'
import { siteUrl } from '../lib/site-url'
import { friendlyAuthError, friendlyThrown, isNetworkError } from '../lib/auth-errors'
import { safeNext } from '../lib/auth/safeNext'
import { CODE_MAX, RESEND_AFTER_MS, TYPED_SETTLE_MS, codeDigits, isWholeCode } from '../lib/auth/emailCode'
import { Field, PillButton } from './ui'
import { authAtoms as a } from './AuthShell'

/** A wrong or spent CODE is not a spent "link": say what the person is holding. */
function codeError(message: string): string {
  if (isNetworkError(message)) return friendlyAuthError(message)
  if (/rate limit|for security purposes|only request this/i.test(message)) return friendlyAuthError(message)
  if (/expired|invalid|otp|token/i.test(message)) {
    return "that code isn't right, or it has expired. if you asked for more than one email, only the newest code works. please check it, or ask for a new one."
  }
  return friendlyAuthError(message)
}

export default function EmailCodeEntry({
  email,
  next = '/dashboard',
  onChangeAddress,
}: {
  /** the address the email went to — verifyOtp needs the pair, not just the code. */
  email: string
  next?: string
  /** puts the address field back, with the address still in it. Offered as a quiet
   *  "use a different address" wherever the surface can do it. */
  onChangeAddress?: () => void
}) {
  const [code, setCode] = useState('')
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState('')
  const [note, setNote] = useState('')
  // "send it again" appears after a wait, and the wait starts over each time it is used
  const [canResend, setCanResend] = useState(false)
  const [resending, setResending] = useState(false)
  const [round, setRound] = useState(0)
  const busy = useRef(false)
  const settle = useRef<number | null>(null)

  useEffect(() => {
    const t = window.setTimeout(() => setCanResend(true), RESEND_AFTER_MS)
    return () => window.clearTimeout(t)
  }, [round])

  const stopSettle = () => {
    if (settle.current != null) window.clearTimeout(settle.current)
    settle.current = null
  }
  useEffect(() => stopSettle, [])

  const verify = async (raw: string) => {
    stopSettle()
    const token = codeDigits(raw)
    if (!isWholeCode(token)) {
      setError("that isn't the whole code yet. please check the email for every digit.")
      return
    }
    if (busy.current) return
    busy.current = true
    setChecking(true)
    setError('')
    setNote('')
    try {
      const supabase = createClient()
      // type 'email' covers what signInWithOtp issues, for a returning person
      // and a brand-new one alike — one verify path for every surface.
      const { error: err } = await supabase.auth.verifyOtp({ email, token, type: 'email' })
      if (!err) {
        // signed in: stay "checking" while the page leaves
        window.location.assign(safeNext(next))
        return
      }
      setError(codeError(err.message))
    } catch (err) {
      setError(friendlyThrown(err))
    }
    // the digits stay where they are — only the verdict is shown
    busy.current = false
    setChecking(false)
  }

  const onChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const digits = codeDigits(ev.target.value)
    const jumped = digits.length - code.length > 1
    setCode(digits)
    stopSettle()
    if (error) setError('')
    if (!isWholeCode(digits)) return
    // dropped in whole by the OS autofill, or the longest code there is: check now.
    // typed: check once the typing settles, since the length is not ours to know.
    if (jumped || digits.length === CODE_MAX) void verify(digits)
    else settle.current = window.setTimeout(() => void verify(digits), TYPED_SETTLE_MS)
  }

  const onPaste = (ev: React.ClipboardEvent<HTMLInputElement>) => {
    const digits = codeDigits(ev.clipboardData.getData('text'))
    // not a code at all (no digits): let the browser do whatever it would have done
    if (!digits) return
    ev.preventDefault()
    stopSettle()
    setCode(digits)
    if (isWholeCode(digits)) void verify(digits)
  }

  const resend = async () => {
    if (resending) return
    setResending(true)
    setError('')
    setNote('')
    try {
      const supabase = createClient()
      // the same email, landing in the same place the first one did
      const { error: err } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: `${siteUrl()}/auth/land?next=${encodeURIComponent(safeNext(next))}` },
      })
      if (err) {
        setError(friendlyAuthError(err.message))
      } else {
        setNote('sent again. please check your inbox, and your spam folder.')
        setCanResend(false)
        setRound((r) => r + 1)
      }
    } catch (err) {
      setError(friendlyThrown(err))
    }
    setResending(false)
  }

  return (
    <form
      onSubmit={(ev) => {
        ev.preventDefault()
        void verify(code)
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}
    >
      <Field
        label="or enter the code from the email"
        name="code"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        maxLength={CODE_MAX + 4}
        placeholder="the digits in the email"
        value={code}
        onChange={onChange}
        onPaste={onPaste}
      />
      {error ? <p role="alert" style={a.err}>{error}</p> : null}
      {note ? <p role="status" style={{ ...a.ok, margin: 0 }}>{note}</p> : null}
      <PillButton type="submit" loading={checking}>
        {checking ? 'checking…' : 'enter'}
      </PillButton>
      {onChangeAddress || canResend ? (
        <div style={{ display: 'flex', gap: '1.4rem', flexWrap: 'wrap', alignItems: 'baseline' }}>
          {onChangeAddress ? (
            <button type="button" className="quiet-link" onClick={onChangeAddress}>
              use a different address
            </button>
          ) : null}
          {canResend ? (
            <button type="button" className="quiet-link" onClick={() => void resend()} disabled={resending}>
              {resending ? 'sending…' : 'send it again'}
            </button>
          ) : null}
        </div>
      ) : null}
    </form>
  )
}
